/**
 * [Phase 2c.6-A.1] LiveActivityFeed
 * ─────────────────────────────────
 * Shared live activity surface for BOTH snake and auction draft rooms.
 *
 * Subscribes to the format's `activityVoice` map (snake: SnakeDraftContext;
 * auction: DraftContext) and renders up to 30 most-recent activities in
 * reverse-chronological order.
 *
 * Standalone Adam-voice activity types are LIVE-dispatched by the backend
 * via _emit_activity_written → ACTIVITY_WRITTEN WS broadcasts. Prior to
 * 2c.6-A.1 these had no draft-time UI home - only the season-side
 * LeagueHome activity feed surfaced them, so users saw Curly's content
 * "a little late" (HF2-11 diagnostic, Hypothesis A confirmed).
 *
 * Feed behavior (per spec §Component behavior):
 *  • type pill color-coded per activity type
 *  • body text truncated at ~140 chars with expand-on-click
 *  • per-row speaker icon (ActivitySpeakerButton) - click plays via singleton
 *  • relative timestamp ("just now", "5s ago", "1m ago")
 *  • auto-scroll-to-top on new activity
 *  • pause-auto-scroll when user actively scrolls down → "↓ N new" pill
 *  • D.6 voice_source regression: only `voice_source === 'llm'` rows render
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import AdamAvatarBadge from "../AdamAvatarBadge";
import ActivitySpeakerButton from "./ActivitySpeakerButton";

const MAX_ROWS = 30;
const TRUNCATE_AT = 140;

// Activity type → color-coded pill style. Spec §Component behavior + A.1.5 + A.1.8.
const TYPE_STYLES = {
  // LLM-source standalone types (auction-side only after A.1.8)
  STEAL_DETECTED:  { label: "STEAL",   bg: "#00C85322", fg: "#00C853" }, // green
  REACH_DETECTED:  { label: "REACH",   bg: "#FF444422", fg: "#FF4444" }, // red
  RUN_STARTED:     { label: "RUN",     bg: "#FF6B0022", fg: "#FF6B00" }, // orange
  ROUND_RECAP:     { label: "RECAP",   bg: "#88888822", fg: "#A0A0A0" }, // neutral
  PICK_MADE:       { label: "PICK",    bg: "#007AFF22", fg: "#007AFF" }, // blue
  // [A.1.8] Snake template-source round boundary marker
  ROUND_START:     { label: "ROUND",   bg: "#88888822", fg: "#A0A0A0" }, // neutral
};

// All auctioneer-source rows render with the same ADAM pill in pylon orange.
const ADAM_PILL = { label: "ADAM", bg: "#FF6B0022", fg: "#FF6B00" };

function formatRelative(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const deltaSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (deltaSec < 5) return "just now";
  if (deltaSec < 60) return `${deltaSec}s ago`;
  const min = Math.floor(deltaSec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  return `${hr}h ago`;
}

export default function LiveActivityFeed({
  activityVoice,
  isDark,
  format = "snake",
  audioControls = null,
  showSpeakerIcons = true,
}) {
  // Re-render every 15s so relative timestamps stay fresh.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 15000);
    return () => clearInterval(id);
  }, []);

  // Sort entries reverse-chronological; cap to MAX_ROWS.
  // [Phase 2c.6-A.1.5] D.6.1 contract amendment: filter accepts both
  // voice_source='llm' (backend LLM writers) AND voice_source='auctioneer'
  // (client-routed auctioneer marquee strings). 'template' fallbacks are
  // still suppressed - that's the D.6 silent-fallback guardrail.
  const rows = useMemo(() => {
    const arr = Object.entries(activityVoice || {}).map(([id, v]) => ({ id, ...v }));
    arr.sort((a, b) => {
      const ta = new Date(a.createdAt || 0).getTime();
      const tb = new Date(b.createdAt || 0).getTime();
      return tb - ta;
    });
    return arr.filter(
      (r) => r.voiceSource === "llm" || r.voiceSource === "auctioneer"
    ).slice(0, MAX_ROWS);
  }, [activityVoice]);

  // Auto-scroll-to-top behavior + "↓ N new" pill while paused.
  const scrollRef = useRef(null);
  const lastTopIdRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // Reset on first row arrival.
  useEffect(() => {
    if (!rows.length) {
      lastTopIdRef.current = null;
      setPaused(false);
      setPendingCount(0);
    }
  }, [rows.length]);

  // When new top row arrives, either auto-scroll up or increment pending.
  useEffect(() => {
    if (!rows.length) return;
    const topId = rows[0].id;
    if (lastTopIdRef.current === topId) return;
    const isFirstObserved = lastTopIdRef.current === null;
    lastTopIdRef.current = topId;
    if (isFirstObserved) return;
    if (paused) {
      setPendingCount((n) => Math.min(n + 1, 99));
    } else {
      // Auto-scroll-to-top.
      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      });
    }
  }, [rows, paused]);

  // User scroll detection → pause when scrolled down.
  // [A.1.7-4] Auto-resume after 10s of no scroll activity (defensive - catches
  // the case where a user scrolled mid-draft and walked away; without this the
  // feed appears "frozen" on early rows for the rest of the session).
  const lastScrollAtRef = useRef(0);
  const autoResumeTimerRef = useRef(null);
  const onScroll = (e) => {
    const el = e.currentTarget;
    lastScrollAtRef.current = Date.now();
    if (el.scrollTop > 24) {
      if (!paused) setPaused(true);
    } else if (paused) {
      setPaused(false);
      setPendingCount(0);
    }
  };
  useEffect(() => {
    if (!paused) {
      clearTimeout(autoResumeTimerRef.current);
      return;
    }
    // Schedule auto-resume sweep.
    const sweep = () => {
      const idle = Date.now() - lastScrollAtRef.current;
      if (idle >= 10000) {
        setPaused(false);
        setPendingCount(0);
        requestAnimationFrame(() => {
          scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        });
      } else {
        autoResumeTimerRef.current = setTimeout(sweep, 10000 - idle + 50);
      }
    };
    autoResumeTimerRef.current = setTimeout(sweep, 10000);
    return () => clearTimeout(autoResumeTimerRef.current);
  }, [paused, pendingCount]);

  const resume = () => {
    setPaused(false);
    setPendingCount(0);
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Theming.
  const cardBg = isDark ? "#1C1C1C" : "#FFFFFF";
  const border = isDark ? "#252525" : "#D8D8D8";
  const text   = isDark ? "#F5F5F5" : "#0A0A0A";
  const muted  = isDark ? "#888888" : "#666666";

  return (
    <div
      className="rounded-2xl border p-3 h-full overflow-hidden flex flex-col"
      style={{ background: cardBg, borderColor: border }}
      data-testid={`live-activity-feed-${format}`}
    >
      <div className="flex items-center justify-between mb-2 shrink-0 gap-2">
        {/* [Phase 2c.6-A.2.1 D-6 / RL-34] Unified Adam badge - pulses when
            audio is unmuted AND there's content to play. */}
        <AdamAvatarBadge
          label="ADAM · LIVE"
          size="xs"
          pulse={!!(audioControls && !audioControls.muted && rows.length > 0)}
          testid="feed-adam-badge"
        />
        <div className="flex items-center gap-2">
          {audioControls && (
            <>
              <button
                onClick={audioControls.toggleMute}
                data-testid={audioControls.muted ? "feed-unmute-btn" : "feed-mute-btn"}
                title={audioControls.muted ? "Unmute Adam" : "Mute Adam"}
                className="transition-colors shrink-0"
                style={{ color: audioControls.muted ? "#888" : "#F5F5F5" }}
              >
                {audioControls.muted
                  ? <VolumeX size={13} />
                  : <Volume2 size={13} />}
              </button>
              <input
                type="range"
                min="0"
                max="100"
                value={audioControls.muted ? 0 : audioControls.volume}
                onChange={audioControls.handleVolumeChange}
                className="w-14 h-1 accent-[#FF6B00] shrink-0 cursor-pointer"
                style={{ appearance: "none" }}
                data-testid="feed-volume-slider"
              />
            </>
          )}
          <span className="font-mono text-[9px] tabular-nums" style={{ color: muted }}>
            {rows.length}/{MAX_ROWS}
          </span>
        </div>
      </div>

      {/* Paused-by-scroll "↓ N new" pill */}
      {paused && pendingCount > 0 && (
        <button
          type="button"
          onClick={resume}
          data-testid="feed-new-pill"
          className="self-center mb-2 px-3 py-1 rounded-full text-[10px] font-display font-black uppercase tracking-widest transition-all shrink-0"
          style={{
            background: "#FF6B00",
            color: "#FFFFFF",
          }}
        >
          ↓ {pendingCount} new
        </button>
      )}

      {/* Scroll region */}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex-1 overflow-y-auto pr-1 space-y-2"
        data-testid="feed-scroll"
      >
        {rows.length === 0 && (
          <p className="text-xs" style={{ color: muted }}>
            Adam's mic is hot. Activity will appear as picks happen.
          </p>
        )}
        {rows.map((row) => (
          <FeedRow
            key={row.id}
            row={row}
            text={text}
            muted={muted}
            border={border}
            isDark={isDark}
            showSpeakerIcons={showSpeakerIcons}
          />
        ))}
      </div>
    </div>
  );
}

function FeedRow({ row, text, muted, border, isDark, showSpeakerIcons }) {
  // [Phase 2c.6-A.1.5] Auctioneer-source rows all render with the unified
  // ADAM pill (pylon orange). LLM-source rows use the existing type-coded
  // pill set. Unknown activity types fall through to a neutral pill.
  let style;
  if (row.voiceSource === "auctioneer") {
    style = ADAM_PILL;
  } else {
    style = TYPE_STYLES[row.activityType] || {
      label: (row.activityType || "ADAM").replace(/_/g, " "),
      bg: "#88888822",
      fg: muted,
    };
  }
  const [expanded, setExpanded] = useState(false);
  const isLong = (row.voiceText || "").length > TRUNCATE_AT;
  const body = !expanded && isLong
    ? row.voiceText.slice(0, TRUNCATE_AT).trimEnd() + "…"
    : row.voiceText;

  return (
    <div
      className="flex items-start gap-2 rounded-xl p-2"
      style={{
        background: isDark ? "#111111" : "#F5F5F5",
        border: `1px solid ${border}`,
      }}
      data-testid={`feed-row-${row.id}`}
    >
      <span
        className="font-display font-black text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-md shrink-0"
        style={{ background: style.bg, color: style.fg }}
        data-testid={`feed-pill-${row.voiceSource === "auctioneer" ? "ADAM" : (row.activityType || "ADAM")}`}
      >
        {style.label}
      </span>
      <div className="min-w-0 flex-1">
        <p
          className="text-xs leading-snug"
          style={{ color: text }}
          onClick={() => isLong && setExpanded((e) => !e)}
        >
          {body}
          {isLong && (
            <span
              className="ml-1 font-display font-bold text-[10px] uppercase tracking-widest cursor-pointer"
              style={{ color: "#FF6B00" }}
              data-testid={`feed-row-expand-${row.id}`}
            >
              {expanded ? "less" : "more"}
            </span>
          )}
        </p>
        <p className="font-mono text-[9px] tabular-nums mt-0.5" style={{ color: muted }}>
          {formatRelative(row.createdAt)}
        </p>
      </div>
      {showSpeakerIcons && (
        <ActivitySpeakerButton
          activityId={row.id}
          voiceText={row.voiceText}
          voiceSource={row.voiceSource}
          testid={`feed-speaker-${row.id}`}
        />
      )}
    </div>
  );
}
